/**
 * Local-first mutations for the courses domain (Space).
 *
 * They write to the local PowerSync DB (Drizzle) → the sync queue sends them
 * to the server (`db/sync.rs`), which applies authz (author/editor, CC-40),
 * seals the course version and RE-GRADES the progress (anti-fraud). The
 * client is NEVER the authority: the final grade/XP come back via sync.
 */

import {
  and,
  block,
  course,
  enrollment,
  eq,
  lesson,
  lesson_progress,
  type PowerSyncDrizzleDB,
  unit,
} from "@space/powersync";
import type { BlockResponse } from "../learning-blocks/types";
import { nowISO } from "../utils/date-helpers";
import { generateId } from "@fleet/utils";
import {
  LocalWriteLostError,
  patchRowVerified,
  type VerifiedWriteOptions,
  waitForRowPresent,
} from "../powersync/verified-write";

// ─── Authoring: course ───────────────────────────────────────────────────────

export interface CreateCourseInput {
  title?: string;
  description?: string;
  category?: string;
  language?: string;
}

/** Creates a draft course. The author is the current user (sync enforces authz). */
export async function createCourseMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  input: CreateCourseInput = {},
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(course).values({
    id,
    author_id: userId,
    title: input.title?.trim() || "Nuevo curso",
    description: input.description ?? null,
    category: input.category ?? null,
    language: input.language ?? "es",
    visibility: "draft",
    version: 1,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * UPDATE of the `course` row that **must not be lost silently**.
 *
 * An `UPDATE … WHERE id = ?` against the PowerSync view can match **ZERO
 * rows and return "success"**. That's what broke `certificates.spec` and
 * `courses-publish.spec`, and it's measured at the base of the local stack
 * (`activity` for course `06a131d0`): the course is created, **1.03 s later**
 * the unit is created, and **in between there is NO `course/updated` op** —
 * the title the author had just typed never made it into a CRUD op. In a
 * course where everything was queued before the first upload (`136ef638`) it
 * IS there: `created` and `updated` separated by 7 ms.
 *
 * The mechanism: when the CRUD queue drains, PowerSync rebuilds the local
 * view from the last checkpoint. If the checkpoint doesn't yet carry the
 * freshly created row, **the row is ABSENT during that instant** — and an
 * UPDATE against it writes nothing, generates no op and does not fail. The
 * author sees their text on screen, the database keeps "Nuevo curso" forever,
 * the course goes out to the catalog under that name and **every certificate
 * issued gets sealed with it**: an accreditation document with the wrong course name.
 *
 * It used to be a `patchCourseRow` function dedicated to this single table.
 * It was converged into the canon's generic `patchRowVerified`
 * (`listto-app@9ae347b6`, the same mechanism first measured HERE) because the
 * same window affects `unit`, `lesson`, `block`, `enrollment` and
 * `lesson_progress` — an audit (P4x) measured that of the 6 course
 * authoring/consumption tables only `course` was protected. `updated_at` is set by `patchRowVerified` on every attempt.
 */
export async function updateCourseMutation(
  db: PowerSyncDrizzleDB,
  courseId: string,
  updates: Partial<{
    title: string;
    description: string | null;
    category: string | null;
    language: string;
  }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const patch = { ...updates } as Record<string, unknown>;
  if (typeof patch.title === "string") {
    patch.title = patch.title.trim() || "Nuevo curso";
  }
  await patchRowVerified(db, course, course.id, courseId, patch, options);
}

/** Publishes the course to the public catalog (guest-first). Seals `published_at`. */
export async function publishCourseMutation(
  db: PowerSyncDrizzleDB,
  courseId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, course, course.id, courseId, { visibility: "public" }, options);
}

/** Reverts the course to draft (removes it from the catalog). */
export async function unpublishCourseMutation(
  db: PowerSyncDrizzleDB,
  courseId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, course, course.id, courseId, { visibility: "draft" }, options);
}

export async function deleteCourseMutation(
  db: PowerSyncDrizzleDB,
  courseId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, course, course.id, courseId, { deleted_at: nowISO() }, options);
}

// ─── Authoring: unit / lesson / block ────────────────────────────────────────

export async function addUnitMutation(
  db: PowerSyncDrizzleDB,
  courseId: string,
  opts: { title?: string; position?: number } = {},
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(unit).values({
    id,
    course_id: courseId,
    title: opts.title?.trim() || "Nueva unidad",
    position: opts.position ?? 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateUnitMutation(
  db: PowerSyncDrizzleDB,
  unitId: string,
  updates: Partial<{ title: string; position: number }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, unit, unit.id, unitId, { ...updates }, options);
}

export async function deleteUnitMutation(
  db: PowerSyncDrizzleDB,
  unitId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, unit, unit.id, unitId, { deleted_at: nowISO() }, options);
}

export async function addLessonMutation(
  db: PowerSyncDrizzleDB,
  params: {
    courseId: string;
    unitId: string;
    title?: string;
    position?: number;
    xpReward?: number;
  },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(lesson).values({
    id,
    unit_id: params.unitId,
    course_id: params.courseId,
    title: params.title?.trim() || "Nueva lección",
    position: params.position ?? 0,
    xp_reward: params.xpReward ?? 10,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateLessonMutation(
  db: PowerSyncDrizzleDB,
  lessonId: string,
  updates: Partial<{ title: string; position: number; xp_reward: number }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, lesson, lesson.id, lessonId, { ...updates }, options);
}

export async function deleteLessonMutation(
  db: PowerSyncDrizzleDB,
  lessonId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, lesson, lesson.id, lessonId, { deleted_at: nowISO() }, options);
}

export async function addBlockMutation(
  db: PowerSyncDrizzleDB,
  params: {
    courseId: string;
    lessonId: string;
    kind: string;
    content: unknown;
    position?: number;
    weight?: number;
  },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(block).values({
    id,
    lesson_id: params.lessonId,
    course_id: params.courseId,
    kind: params.kind,
    position: params.position ?? 0,
    weight: params.weight ?? 1,
    content: JSON.stringify(params.content ?? {}),
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateBlockMutation(
  db: PowerSyncDrizzleDB,
  blockId: string,
  updates: Partial<{ kind: string; content: unknown; position: number; weight: number }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const patch: Record<string, unknown> = {};
  if (updates.kind !== undefined) {
    patch.kind = updates.kind;
  }
  if (updates.position !== undefined) {
    patch.position = updates.position;
  }
  if (updates.weight !== undefined) {
    patch.weight = updates.weight;
  }
  if (updates.content !== undefined) {
    patch.content = JSON.stringify(updates.content);
  }
  await patchRowVerified(db, block, block.id, blockId, patch, options);
}

export async function deleteBlockMutation(
  db: PowerSyncDrizzleDB,
  blockId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, block, block.id, blockId, { deleted_at: nowISO() }, options);
}

// ─── Consumption: enrollment + progress ──────────────────────────────────────

/** Enrolls the user in a course (idempotent via the server's unique constraint). */
export async function enrollMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  courseId: string,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(enrollment).values({
    id,
    user_id: userId,
    course_id: courseId,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function unenrollMutation(
  db: PowerSyncDrizzleDB,
  enrollmentId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    enrollment,
    enrollment.id,
    enrollmentId,
    { deleted_at: nowISO() },
    options,
  );
}

export interface SaveLessonProgressInput {
  progressId?: string;
  userId: string;
  courseId: string;
  lessonId: string;
  status: "in_progress" | "completed";
  lastBlockId?: string;
  /** Locally claimed grade (0..1). The server re-verifies it; informational only. */
  claimedScore?: number;
  responses?: BlockResponse[];
  startedAt?: number;
  finishedAt?: number;
}

/** `waitForRowPresent` without throwing: `true`/`false` instead of `LocalWriteLostError`. */
async function rowEventuallyPresent(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<boolean> {
  try {
    await waitForRowPresent(db, lesson_progress, lesson_progress.id, id, options);
    return true;
  } catch (err) {
    if (err instanceof LocalWriteLostError) {
      return false;
    }
    throw err;
  }
}

/**
 * Persists a lesson's progress. Sends the `responses` (JSON string) and the
 * claimed grade so the server RE-GRADES in the write-path and computes the
 * authoritative XP. Returns the `lesson_progress` row id (stable per lesson,
 * for resuming). The client can paint immediate feedback with
 * `applyLessonCompletion`, but the final grade/XP arrive via sync.
 *
 * `progress` is the student: silently losing this write doesn't lose a text
 * field, it loses the advancement or duplicates it. Two distinct windows,
 * both with the same root cause (the view rebuilds from the checkpoint):
 *
 * 1. Known row (`existing`/`progressId`) and the UPDATE doesn't match →
 *    covered by `patchRowVerified`, same as the rest of this module's tables.
 * 2. The alternate-key read (`user_id`+`lesson_id`) that resolves the id can
 *    come back EMPTY while the row from an earlier save in this same session
 *    hasn't landed yet — it's not a student starting the lesson, it's the
 *    window seen from a SELECT instead of an UPDATE. Without verifying, the
 *    `else` branch below would generate a new id (or reuse the known
 *    `progressId` against a row that actually DOES exist, colliding with the
 *    PK) and leave the progress duplicated or the save failing where it
 *    shouldn't. If the caller already carries a `progressId`, it waits (with
 *    backoff, same as `waitForRowPresent`) for the row to reappear before
 *    assuming it doesn't exist.
 */
export async function saveLessonProgressMutation(
  db: PowerSyncDrizzleDB,
  input: SaveLessonProgressInput,
  options?: VerifiedWriteOptions,
): Promise<string> {
  const now = nowISO();
  // PowerSync exposes local tables as VIEWS and does NOT support UPSERT
  // (`ON CONFLICT`) on them → a `.onConflictDoUpdate()` throws "cannot UPSERT
  // a view" and the progress was NOT being written (neither local nor sync).
  // We resolve the STABLE row by (user_id, lesson_id) and do a manual UPDATE
  // or INSERT — a single row per lesson (resuming via last_block_id) without depending on UPSERT.
  const patch = {
    user_id: input.userId,
    course_id: input.courseId,
    lesson_id: input.lessonId,
    status: input.status,
    last_block_id: input.lastBlockId ?? null,
    claimed_score: input.claimedScore ?? null,
    responses: input.responses ? JSON.stringify(input.responses) : null,
    started_at: input.startedAt ?? null,
    finished_at: input.finishedAt ?? null,
  };

  const existing = await db
    .select({ id: lesson_progress.id })
    .from(lesson_progress)
    .where(
      and(eq(lesson_progress.user_id, input.userId), eq(lesson_progress.lesson_id, input.lessonId)),
    )
    .limit(1);

  if (existing[0]) {
    await patchRowVerified(db, lesson_progress, lesson_progress.id, existing[0].id, patch, options);
    return existing[0].id;
  }

  if (input.progressId && (await rowEventuallyPresent(db, input.progressId, options))) {
    await patchRowVerified(
      db,
      lesson_progress,
      lesson_progress.id,
      input.progressId,
      patch,
      options,
    );
    return input.progressId;
  }

  const id = input.progressId ?? generateId();
  await db.insert(lesson_progress).values({ id, ...patch, updated_at: now, created_at: now });
  return id;
}
